import {
  WEEKDAY_CHART_PRESETS,
  WEEKLY_CHART_PRESETS,
  SINGLE_DAY_PRESETS
} from "./widgetConstants";

/**
 * Groups raw rate series data into chart-ready buckets for the given preset.
 * TODAY/YESTERDAY have exactly one day in range, so they get a single-point
 * bucket (one x-axis category) instead of being grouped into weekday/weekly buckets.
 * If the API already returns pre-grouped chartData, pass it through directly.
 */
export const buildChartData = (series = [], preset) => {
  if (SINGLE_DAY_PRESETS.has(preset)) {
    return buildSingleDayBucket(series, preset);
  }

  if (WEEKDAY_CHART_PRESETS.has(preset) || WEEKLY_CHART_PRESETS.has(preset)) {
    return buildDailyPoints(series);
  }

  return [];
};

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SHORT_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// e.g. "Jun 17 (Wed)"
const formatDateWithDay = date => `${SHORT_MONTHS[date.getMonth()]} ${date.getDate()} (${SHORT_WEEKDAYS[date.getDay()]})`;

const pointValue = point => point.count != null ? point.count : (point.rate || 0);

// TODAY/YESTERDAY have exactly one day in range, so all raw points (e.g. hourly
// activity within that day) are summed into a single chart point.
const buildSingleDayBucket = (series, preset) => {
  const date = new Date();
  if (preset === "yesterday") date.setDate(date.getDate() - 1);
  const sum = series.reduce((total, point) => total + pointValue(point), 0);
  return [{ label: formatDateWithDay(date), value: parseFloat(sum.toFixed(2)) }];
};

// "7d"/"lastWeek" cover exactly 7 distinct days, so this is a 1:1 mapping
// (no aggregation) - just attaches the date+weekday label to each day's value.
const buildDailyPoints = series =>
  series.map(point => {
    const date = new Date(point.date || point.activityDate);
    return { label: formatDateWithDay(date), value: pointValue(point) };
  });

/**
 * If the API returns pre-grouped chartData array, use it directly.
 * Falls back to buildChartData for raw series.
 */
export const resolveChartData = (apiResponse, preset) => {
  if (apiResponse?.chartData?.length) {
    return apiResponse.chartData;
  }
  return buildChartData(apiResponse?.series || [], preset);
};
